import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const cognitoConfig = {
  clientId: process.env.REACT_APP_AWS_COGNITO_CLIENT_ID,
  domain: process.env.REACT_APP_AWS_COGNITO_MYRIAM_DOMAIN,
  redirectUri:
    process.env.REACT_APP_AWS_COGNITO_REDIRECT_URI ||
    `${window.location.origin}`,
  identityProvider: process.env.REACT_APP_AWS_COGNITO_MYRIAM_IDP,
  userPoolId: process.env.REACT_APP_AWS_COGNITO_USER_POOL_ID,
  region: process.env.REACT_APP_AWS_COGNITO_REGION,
  idpInitiated: process.env.REACT_APP_SAML_IDP_INITIATED === 'true',
  idpUrl: process.env.REACT_APP_SAML_IDP_URL,
  spId: process.env.REACT_APP_SAML_SP_ID,
};

export const useCognitoSaml = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error parsing JWT:', e);
      return null;
    }
  };

  const loginWithMYRIAM = useCallback(() => {
    console.log('[SAML] loginWithMYRIAM called', {
      idpInitiated: cognitoConfig.idpInitiated,
      hasIdpUrl: !!cognitoConfig.idpUrl,
      hasSpId: !!cognitoConfig.spId,
      redirectUri: cognitoConfig.redirectUri,
      domain: cognitoConfig.domain,
    });

    // Check if IdP-initiated flow should be used (direct redirect to IdP with SPID)
    if (
      cognitoConfig.idpInitiated &&
      cognitoConfig.idpUrl &&
      cognitoConfig.spId
    ) {
      const url = new URL(cognitoConfig.idpUrl);
      url.searchParams.set('SPID', cognitoConfig.spId);

      const returnUrl = window.location.hash.replace('#', '') || '/';
      sessionStorage.setItem('saml_return_url', returnUrl);

      console.log('[SAML] IdP-initiated flow: redirecting to IdP', {
        idpUrl: url.toString().replace(/SPID=[^&]+/, 'SPID=***'),
        storedReturnUrl: returnUrl,
      });

      window.location.href = url.toString();
      return;
    }

    const loginUrl =
      `https://${cognitoConfig.domain}/oauth2/authorize?` +
      `identity_provider=${cognitoConfig.identityProvider}&` +
      `redirect_uri=${encodeURIComponent(cognitoConfig.redirectUri)}&` +
      `response_type=CODE&` +
      `client_id=${cognitoConfig.clientId}&` +
      `scope=openid%20email%20profile`;

    console.log('[SAML] SP-initiated flow: redirecting to Cognito', {
      loginUrl: loginUrl.replace(/code=[^&]*/g, ''),
      redirectUri: cognitoConfig.redirectUri,
    });

    window.location.href = loginUrl;
  }, []);

  const exchangeCodeForTokens = useCallback(async (code) => {
    const tokenUrl = `https://${cognitoConfig.domain}/oauth2/token`;

    console.log('[SAML] exchangeCodeForTokens: starting', {
      tokenUrl,
      codeLength: code?.length,
      redirectUri: cognitoConfig.redirectUri,
      hasClientId: !!cognitoConfig.clientId,
    });

    try {
      const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: cognitoConfig.clientId,
        code,
        redirect_uri: cognitoConfig.redirectUri,
      });

      const response = await axios.post(tokenUrl, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { data } = response;
      const hasIdToken = !!data?.id_token;
      const hasAccessToken = !!data?.access_token;
      const hasRefreshToken = !!data?.refresh_token;

      console.log('[SAML] exchangeCodeForTokens: success', {
        hasIdToken,
        hasAccessToken,
        hasRefreshToken,
        tokenType: data?.token_type,
        expiresIn: data?.expires_in,
      });

      return data;
    } catch (err) {
      console.error('[SAML] exchangeCodeForTokens: failed', {
        error: err?.message,
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        responseData: err?.response?.data,
      });
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('cognito_id_token');
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_refresh_token');
    localStorage.removeItem('cognito_user');
    setUser(null);

    const logoutUrl =
      `https://${cognitoConfig.domain}/logout?` +
      `client_id=${cognitoConfig.clientId}&` +
      `logout_uri=${encodeURIComponent(window.location.origin)}`;

    window.location.href = logoutUrl;
  }, []);

  const handleCallback = useCallback(async () => {
    setLoading(true);
    setError(null);

    console.log('[SAML] handleCallback: starting', {
      fullUrl: window.location.href,
      search: window.location.search,
      hash: window.location.hash,
    });

    try {
      // Check both window.location.search (direct Cognito redirect) and
      // hash-based query params (after index.js redirect for HashRouter)
      let urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.get('code') && window.location.hash.includes('?')) {
        const hashQuery = window.location.hash.split('?')[1];
        urlParams = new URLSearchParams(hashQuery);
        console.log('[SAML] handleCallback: using params from hash', {
          hashQuery,
        });
      }

      const code = urlParams.get('code');
      const errorParam = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      const state = urlParams.get('state');

      console.log('[SAML] handleCallback: URL params', {
        hasCode: !!code,
        codeLength: code?.length,
        errorParam,
        errorDescription,
        hasState: !!state,
      });

      if (errorParam) {
        console.error('[SAML] handleCallback: IdP/Cognito returned error', {
          error: errorParam,
          errorDescription,
        });
        throw new Error(errorDescription || errorParam);
      }

      if (!code) {
        console.error('[SAML] handleCallback: no authorization code received');
        throw new Error('No se recibió código de autorización');
      }

      const tokens = await exchangeCodeForTokens(code);

      const payload = parseJwt(tokens.id_token);

      if (!payload) {
        console.error('[SAML] handleCallback: failed to parse JWT payload');
        throw new Error('Error al decodificar token');
      }

      // SAML IdP (e.g. Myriam) may put email in identities[0].userId instead of payload.email
      const email =
        payload?.email ||
        payload?.identities?.[0]?.userId ||
        payload['cognito:username']?.split('_').pop() ||
        null;

      const userData = {
        email,
        name: payload.name,
        givenName: payload.given_name,
        familyName: payload.family_name,
        locale: payload.locale,
        sub: payload.sub,
      };

      console.log('[SAML] handleCallback: user parsed from token', {
        email: userData.email,
        name: userData.name,
        sub: `${userData.sub?.substring(0, 20)}...`,
        exp: payload.exp,
        expDate: payload.exp
          ? new Date(payload.exp * 1000).toISOString()
          : null,
      });

      localStorage.setItem('cognito_id_token', tokens.id_token);
      localStorage.setItem('cognito_access_token', tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem('cognito_refresh_token', tokens.refresh_token);
      }
      localStorage.setItem('cognito_user', JSON.stringify(userData));

      console.log('[SAML] handleCallback: tokens stored in localStorage', {
        hasIdToken: !!localStorage.getItem('cognito_id_token'),
        hasAccessToken: !!localStorage.getItem('cognito_access_token'),
        hasRefreshToken: !!localStorage.getItem('cognito_refresh_token'),
        hasUser: !!localStorage.getItem('cognito_user'),
      });

      setUser(userData);
      setLoading(false);

      return { tokens, user: userData };
    } catch (err) {
      console.error('[SAML] handleCallback: error', {
        message: err?.message,
        stack: err?.stack,
      });
      setError(err.message || 'Error al procesar autenticación');
      setLoading(false);
      throw err;
    }
  }, [exchangeCodeForTokens]);

  const getCurrentUser = useCallback(() => {
    try {
      const storedUser = localStorage.getItem('cognito_user');
      const idToken = localStorage.getItem('cognito_id_token');

      if (storedUser && idToken) {
        const userData = JSON.parse(storedUser);

        const payload = parseJwt(idToken);
        if (payload && payload.exp * 1000 > Date.now()) {
          setUser(userData);
          return userData;
        }
        logout();
        return null;
      }
      return null;
    } catch (err) {
      console.error('Error al obtener usuario:', err);
      return null;
    }
  }, []);

  const isAuthenticated = useCallback(() => {
    const idToken = localStorage.getItem('cognito_id_token');
    if (!idToken) return false;

    const payload = parseJwt(idToken);
    return payload && payload.exp * 1000 > Date.now();
  }, []);

  const getIdToken = useCallback(
    () => localStorage.getItem('cognito_id_token'),
    []
  );

  const getAccessToken = useCallback(
    () => localStorage.getItem('cognito_access_token'),
    []
  );

  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  return {
    user,
    loading,
    error,
    loginWithMYRIAM,
    handleCallback,
    getCurrentUser,
    isAuthenticated,
    getIdToken,
    getAccessToken,
    logout,
  };
};
